import * as React from 'react'

import {
  CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
  EDocumentComparisonChangeType,
  ETargetVersion,
  FOCUSED_HIGHLIGHT_CLASS_NAME,
  FOCUSED_HIGHLIGHT_CONTENT,
  VERSION_COMPARISON_DEFAULT_PAGE,
} from '../../constants'
import { useVersionComparisonStore } from '../../store'
import { THighlightEventDetail, TSelectedVersion } from '../../types'
import { IPDFCompareChangeItem } from '../types'
import { applyPdfDiffHighlights } from '../utils'

/**
 * Zoom scale limits and step size for PDF viewer
 */
const ZOOM_MIN = 0.5 // 50% minimum zoom
const ZOOM_MAX = 3 // 300% maximum zoom
const ZOOM_STEP = 0.25 // 25% increment per zoom action
const VERSION_COMPARISON_MIN_PAGE = 1

/**
 * Options for configuring the PDF controller hook
 */
type UsePDFControllerOptions = {
  signedUrl?: string // Signed URL to fetch PDF from
  version: TSelectedVersion // Current version being viewed
  allVersionsReady: boolean // Whether all versions are ready for sync operations
  isEnabled: boolean // Whether this controller is active
  compareDocumentDiff?: IPDFCompareChangeItem[] | null // PDF diff ops
  scrollContainerRef?: React.RefObject<HTMLDivElement> // Scroll container ref
}

/**
 * Return type for the PDF controller hook
 * Includes page navigation, zoom controls, and PDF lifecycle handlers
 */
type UsePDFControllerHookResult = {
  currentPage: number // Current page displayed (1-indexed)
  setCurrentPage: React.Dispatch<React.SetStateAction<number>> // Setter for current page
  totalPages: number // Total number of pages in PDF
  pdfError: Error | null // Error loading PDF, if any
  zoomScale: number // Current zoom scale (0.5 to 3.0)
  zoomIn: () => void // Increase zoom level
  zoomOut: () => void // Decrease zoom level
  canZoomIn: boolean // Whether zoom in is possible
  canZoomOut: boolean // Whether zoom out is possible
  canGoNext: boolean // Whether next page button is enabled
  canGoPrevious: boolean // Whether previous page button is enabled
  isDisabledChangePage: boolean // Whether page input is disabled
  handlePageChange: (value: number | undefined) => void // Handle manual page input
  handleNextPage: () => void // Navigate to next page
  handlePreviousPage: () => void // Navigate to previous page
  handleDocumentLoadSuccess: (params: { numPages: number }) => void // Called when PDF loads
  handleDocumentLoadError: (error: Error) => void // Called when PDF fails to load
  handlePageRenderSuccess: () => void // Called when page finishes rendering
}

/**
 * Custom hook for managing PDF document state and interactions
 * Handles page navigation, zoom control, and lifecycle events (load/render)
 * Coordinates with global version comparison store to sync state across multiple viewers
 *
 * @param options - Configuration options for the controller
 * @returns Object containing all PDF control methods and state
 */
export const usePDFController = ({
  signedUrl,
  version,
  allVersionsReady,
  isEnabled,
  compareDocumentDiff,
  scrollContainerRef,
}: UsePDFControllerOptions): UsePDFControllerHookResult => {
  const {
    zoomScale,
    currentPage: globalCurrentPage,
    totalPages: globalTotalPages,
    setVersionReady,
    setZoomScale,
    setCurrentPage: setGlobalCurrentPage,
    setTotalPages: setVersionTotalPages,
  } = useVersionComparisonStore()

  const versionId = version?.id ?? ''

  const [currentPage, setCurrentPage] =
    React.useState<number>(globalCurrentPage)
  const [totalPages, setTotalPages] = React.useState<number>(
    VERSION_COMPARISON_DEFAULT_PAGE
  )
  const [pdfError, setPdfError] = React.useState<Error | null>(null)

  const hasMarkedReadyRef = React.useRef(false)
  const pendingFocusUuidRef = React.useRef<string | null>(null)

  const ensureFocusedHighlightStyles = React.useCallback(() => {
    const container = scrollContainerRef?.current
    if (!container) return
    const existingStyle = container.querySelector(
      'style[data-focused-highlight]'
    )
    if (existingStyle) return

    const style = document.createElement('style')
    style.dataset.pdfFocusedHighlight = 'true'
    style.textContent = FOCUSED_HIGHLIGHT_CONTENT
    container.appendChild(style)
  }, [scrollContainerRef])

  const removeFocusedHighlight = React.useCallback(() => {
    const container = scrollContainerRef?.current
    if (!container) return

    container
      .querySelectorAll(`.${FOCUSED_HIGHLIGHT_CLASS_NAME}`)
      .forEach((element) => {
        element.classList.remove(FOCUSED_HIGHLIGHT_CLASS_NAME)
      })
  }, [scrollContainerRef])

  const focusHighlightByUuid = React.useCallback(
    (uuid: string) => {
      const container = scrollContainerRef?.current
      if (!container) return

      ensureFocusedHighlightStyles()
      removeFocusedHighlight()

      const target = container.querySelector(
        `.react-pdf__Page[data-page-number="${currentPage}"] [data-uuid="${uuid}"]`
      )
      if (!target) return

      target.classList.add(FOCUSED_HIGHLIGHT_CLASS_NAME)

      const elementRect = target.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      const targetScrollTop =
        container.scrollTop +
        (elementRect.top - containerRect.top) -
        container.clientHeight / 2 +
        elementRect.height / 2

      const targetScrollLeft =
        container.scrollLeft +
        (elementRect.left - containerRect.left) -
        container.clientWidth / 2 +
        elementRect.width / 2

      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        left: Math.max(0, targetScrollLeft),
        behavior: 'smooth',
      })
    },
    [
      currentPage,
      ensureFocusedHighlightStyles,
      removeFocusedHighlight,
      scrollContainerRef,
    ]
  )

  const applyHighlights = React.useCallback(() => {
    if (!isEnabled) return
    if (!scrollContainerRef?.current) return

    const pageContainer = scrollContainerRef.current.querySelector<HTMLElement>(
      `.react-pdf__Page[data-page-number="${currentPage}"]`
    )
    if (!pageContainer) return

    applyPdfDiffHighlights({
      pageContainer,
      diffOps: compareDocumentDiff ?? [],
      pageNumber: currentPage,
      scale: zoomScale,
      isSource: version?.isSource,
      isTarget: version?.isTarget,
    })

    if (pendingFocusUuidRef.current) {
      focusHighlightByUuid(pendingFocusUuidRef.current)
      pendingFocusUuidRef.current = null
    }
  }, [
    compareDocumentDiff,
    currentPage,
    focusHighlightByUuid,
    isEnabled,
    scrollContainerRef,
    version?.isSource,
    version?.isTarget,
    zoomScale,
  ])

  /**
   * Effect: Reset state when controller is enabled/disabled or when signed URL changes
   * Marks the version as not ready when content is being reloaded
   */
  React.useEffect(() => {
    if (!isEnabled || !versionId) return

    setPdfError(null)
    hasMarkedReadyRef.current = false
    setVersionReady(versionId, false)
  }, [isEnabled, setVersionReady, signedUrl, versionId])

  /**
   * Effect: Clamp current page to valid range when total pages changes
   * Prevents showing a page number that exceeds the total page count
   */
  React.useEffect(() => {
    if (totalPages < VERSION_COMPARISON_MIN_PAGE) return
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  /**
   * Clamp page to this viewer's actual total pages
   * Ensures this viewer doesn't navigate beyond its own page count
   */
  const clampToLocalTotal = React.useCallback(
    (page: number) => {
      return Math.min(Math.max(page, VERSION_COMPARISON_MIN_PAGE), totalPages)
    },
    [totalPages]
  )

  const updateTotalPages = React.useCallback(
    (numPages: number) => {
      setTotalPages(numPages)
      setPdfError(null)
      setVersionTotalPages(versionId, numPages)
    },
    [setVersionTotalPages, versionId]
  )

  const handleDocumentLoadSuccess = React.useCallback(
    ({ numPages }: { numPages: number }) => {
      updateTotalPages(numPages)
    },
    [updateTotalPages]
  )

  const handleDocumentLoadError = React.useCallback((error: Error) => {
    setPdfError(error)
  }, [])

  const handlePageRenderSuccess = React.useCallback(() => {
    if (!isEnabled || !versionId || hasMarkedReadyRef.current) return

    setVersionReady(versionId, true)
    hasMarkedReadyRef.current = true
    applyHighlights()
  }, [applyHighlights, isEnabled, setVersionReady, versionId])

  const handlePageChange = React.useCallback(
    (value: number | undefined) => {
      const normalizedValue = value ?? VERSION_COMPARISON_DEFAULT_PAGE
      // Clamp to this viewer's actual pages, not global total
      const clampedValue = clampToLocalTotal(normalizedValue)
      setGlobalCurrentPage(clampedValue)
    },
    [clampToLocalTotal, setGlobalCurrentPage]
  )

  const handleNextPage = React.useCallback(() => {
    const nextPage = clampToLocalTotal(
      currentPage + VERSION_COMPARISON_MIN_PAGE
    )
    setGlobalCurrentPage(nextPage)
  }, [clampToLocalTotal, currentPage, setGlobalCurrentPage])

  const handlePreviousPage = React.useCallback(() => {
    const nextPage = clampToLocalTotal(
      currentPage - VERSION_COMPARISON_MIN_PAGE
    )
    setGlobalCurrentPage(nextPage)
  }, [clampToLocalTotal, currentPage, setGlobalCurrentPage])

  /**
   * Compute: Check if user can navigate to next page
   * Check against this viewer's actual total pages
   */
  const canGoNext = React.useMemo(
    () => currentPage < totalPages,
    [currentPage, totalPages]
  )

  /**
   * Compute: Check if user can navigate to previous page
   */
  const canGoPrevious = React.useMemo(
    () => currentPage > VERSION_COMPARISON_MIN_PAGE,
    [currentPage]
  )

  /**
   * Compute: Check if page input should be disabled
   * Disabled when there's only one page (no navigation needed)
   * Uses this viewer's actual total pages, not global total
   */
  const isDisabledChangePage = React.useMemo(
    () => totalPages <= VERSION_COMPARISON_MIN_PAGE,
    [totalPages]
  )

  const zoomIn = React.useCallback(() => {
    if (!isEnabled || !allVersionsReady) return

    const newScale = Math.min(zoomScale + ZOOM_STEP, ZOOM_MAX)
    setZoomScale(Math.round(newScale * 100) / 100)
  }, [allVersionsReady, isEnabled, setZoomScale, zoomScale])

  const zoomOut = React.useCallback(() => {
    if (!isEnabled || !allVersionsReady) return

    const newScale = Math.max(zoomScale - ZOOM_STEP, ZOOM_MIN)
    setZoomScale(Math.round(newScale * 100) / 100)
  }, [allVersionsReady, isEnabled, setZoomScale, zoomScale])

  /**
   * Compute: Check if zoom in is possible
   */
  const canZoomIn = React.useMemo(() => zoomScale < ZOOM_MAX, [zoomScale])

  /**
   * Compute: Check if zoom out is possible
   */
  const canZoomOut = React.useMemo(() => zoomScale > ZOOM_MIN, [zoomScale])

  React.useEffect(() => {
    if (!isEnabled) return
    if (!allVersionsReady) return
    if (!compareDocumentDiff) return

    const container = scrollContainerRef?.current
    if (!container) return

    const handleScrollToHighlight = (event: Event) => {
      const customEvent = event as CustomEvent<THighlightEventDetail>
      const { uuid, targetVersion, type } = customEvent.detail || {}
      if (!uuid || !targetVersion) return

      const isReplaceChange = type === EDocumentComparisonChangeType.REPLACE

      if (isReplaceChange) {
        focusHighlightByUuid(uuid)
      } else {
        removeFocusedHighlight()
      }

      const isTargetVersion =
        (targetVersion === ETargetVersion.SOURCE && version?.isSource) ||
        (targetVersion === ETargetVersion.TARGET && version?.isTarget)
      if (!isTargetVersion) return

      const diff = compareDocumentDiff.find((item) => item.uuid === uuid)
      if (!diff?.pageIndex) return

      if (diff.pageIndex !== currentPage) {
        pendingFocusUuidRef.current = uuid
        setGlobalCurrentPage(
          Math.min(
            Math.max(diff.pageIndex, VERSION_COMPARISON_MIN_PAGE),
            globalTotalPages
          )
        )
        return
      }

      focusHighlightByUuid(uuid)
    }

    globalThis.addEventListener(
      CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
      handleScrollToHighlight
    )

    return () => {
      globalThis.removeEventListener(
        CUSTOM_EVENT_SCROLL_TO_HIGHLIGHT,
        handleScrollToHighlight
      )
    }
  }, [
    allVersionsReady,
    compareDocumentDiff,
    currentPage,
    focusHighlightByUuid,
    globalTotalPages,
    isEnabled,
    removeFocusedHighlight,
    scrollContainerRef,
    setGlobalCurrentPage,
    version?.isSource,
    version?.isTarget,
  ])

  React.useEffect(() => {
    applyHighlights()
  }, [applyHighlights])

  /**
   * Effect: Sync local page to global page, clamped by local totalPages
   * Display page = MIN(globalCurrentPage, localTotalPages)
   */
  React.useEffect(() => {
    if (!isEnabled) return

    const displayPage = Math.min(globalCurrentPage, totalPages)
    if (currentPage !== displayPage) setCurrentPage(displayPage)
  }, [currentPage, globalCurrentPage, isEnabled, totalPages])

  /**
   * Memoize and return all control methods and state
   * This prevents unnecessary re-renders of child components that use this hook
   */
  return React.useMemo(
    () => ({
      currentPage,
      setCurrentPage,
      totalPages,
      pdfError,
      zoomScale,
      zoomIn,
      zoomOut,
      canZoomIn,
      canZoomOut,
      canGoNext,
      canGoPrevious,
      isDisabledChangePage,
      handlePageChange,
      handleNextPage,
      handlePreviousPage,
      handleDocumentLoadSuccess,
      handleDocumentLoadError,
      handlePageRenderSuccess,
    }),
    [
      canGoNext,
      canGoPrevious,
      canZoomIn,
      canZoomOut,
      currentPage,
      handleDocumentLoadError,
      handleDocumentLoadSuccess,
      handleNextPage,
      handlePageChange,
      handlePageRenderSuccess,
      handlePreviousPage,
      isDisabledChangePage,
      pdfError,
      setCurrentPage,
      totalPages,
      zoomIn,
      zoomOut,
      zoomScale,
    ]
  )
}
